import { storeToRefs } from 'pinia';
import { computed, onUnmounted, ref, watch } from 'vue';
import type { SortOrder } from '@/shared/lib';

import {
  type DocumentRevision,
  type DocumentSelectionRow,
  type DocumentSelectionSearchField,
  type SearchDocument,
  buildDocumentSelectionRows,
  buildDocumentSelectionSearchParams,
  filterExpandedDocumentIds,
  formatRevisionLabel,
  getAvailableSelectionRevisions,
  getVisibleSelectionRevisions,
  isSelectionRowExpanded,
  syncSelectedRevisionRecords,
  toggleExpandedDocumentIds,
  useDocumentRevisionsStore,
} from '@/entities/Document';
import { DEFAULT_PAGE_SIZE, QUANTITY_OPTIONS, SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
import { type OrderOptions, useInfiniteScrollObserver } from '@/shared/lib';

import { usePreviousRevisionsCache } from './usePreviousRevisionsCache';
import { useDocumentRevisionSelection } from './useDocumentRevisionSelection';

export type { DocumentSelectionRow };

export type DocumentSelectionMode = 'single' | 'multiple';

interface UseDocumentSelectionModalOptions {
  selectionMode?: DocumentSelectionMode;
  maxSelections?: number;
  amountOptions?: Array<{ id: number; name: string }>;
  defaultAmount?: number;
  enableSort?: boolean;
  enableAdvancedFilter?: boolean;
  alreadySelectedRevisionIds?: number[];
  initialSelectedRevisionId?: number | null;
  initialSelectedRevisionIds?: number[];
  initialSelectedRevisions?: DocumentRevision[];
  selectionLimitMessage?: string;
  searchUrl?: string;
  orderOptions?: OrderOptions[];
  fixedFilters?: Record<string, unknown>;
  onDemandRevisions?: boolean;
}

const ORDER_OPTIONS: OrderOptions[] = [
  { label: 'Arquivo mais recente', sort: 'effectiveDate', order: 'desc' },
  { label: 'Arquivo mais antigo', sort: 'effectiveDate', order: 'asc' },
  { label: 'Ordem alfabética crescente', sort: 'name', order: 'asc' },
  { label: 'Ordem alfabética decrescente', sort: 'name', order: 'desc' },
  { label: 'Maior número de revisões', sort: 'revisionCount', order: 'desc' },
  { label: 'Menor número de revisões', sort: 'revisionCount', order: 'asc' },
  { label: 'Do menor para o maior', sort: 'prefix', order: 'asc' },
  { label: 'Do maior para o menor', sort: 'prefix', order: 'desc' },
];

export function useDocumentSelectionModal({
  selectionMode = 'single',
  maxSelections,
  amountOptions = QUANTITY_OPTIONS,
  defaultAmount = DEFAULT_PAGE_SIZE,
  enableSort = false,
  enableAdvancedFilter = false,
  alreadySelectedRevisionIds = [],
  initialSelectedRevisionId = null,
  initialSelectedRevisionIds = [],
  initialSelectedRevisions = [],
  selectionLimitMessage = 'O número máximo de documentos selecionados foi atingido.',
  searchUrl,
  orderOptions = ORDER_OPTIONS,
  fixedFilters = {},
  onDemandRevisions = false,
}: UseDocumentSelectionModalOptions = {}) {
  const documentRevisionsStore = useDocumentRevisionsStore();
  const {
    allRevisions,
    hasMoreRevisions,
    loading,
    loadingMore,
    metadata,
    revisions: results,
  } = storeToRefs(documentRevisionsStore);

  const searchFields = [
    { id: 'prefix' as const, name: 'Prefixo' },
    { id: 'name' as const, name: 'Título' },
  ];

  const searchTerm = ref('');
  const searchFieldId = ref<DocumentSelectionSearchField>('prefix');
  const selectedAmount = ref(defaultAmount);
  const currentPage = ref(1);
  const expandedDocumentIds = ref<number[]>([]);
  const sort = ref<{ sort: string; order: SortOrder | '' }>({ sort: '', order: '' });
  const activeFilters = ref<Record<string, unknown>>({});

  const currentSort = computed(() => sort.value.sort);
  const currentOrder = computed(() => sort.value.order);

  const isInfiniteScrollMode = computed(() => selectedAmount.value === SHOW_ALL_ITEMS_QUANTITY);

  const isFilterActive = computed(() => Object.keys(activeFilters.value).length > 0);

  const documents = computed<SearchDocument[]>(() =>
    isInfiniteScrollMode.value ? allRevisions.value : results.value
  );

  const documentRows = computed<DocumentSelectionRow[]>(() =>
    buildDocumentSelectionRows(documents.value)
  );

  const availableRevisions = computed(() => getAvailableSelectionRevisions(documentRows.value));

  const {
    getState: getPreviousRevisionsState,
    ensureLoaded: ensurePreviousRevisionsLoaded,
    retry: retryLoadPreviousRevisions,
    reset: resetPreviousRevisionsCache,
  } = usePreviousRevisionsCache();

  function isDocumentExpanded(row: DocumentSelectionRow): boolean {
    return isSelectionRowExpanded(row, expandedDocumentIds.value, (revision) =>
      selectionMode === 'multiple'
        ? selectedRevisionIds.value.includes(revision.id)
        : revision.id === selectedRevisionId.value
    );
  }

  const visibleRevisions = computed(() =>
    getVisibleSelectionRevisions(documentRows.value, isDocumentExpanded)
  );

  const {
    allVisibleRevisionsSelected,
    canAddMore,
    canSelectRevision,
    clearSelectionLimitError,
    handleRevisionSelected,
    isRevisionActive,
    isRevisionAlreadyAdded,
    isRevisionSelected,
    selectedRevision,
    selectedRevisionCount,
    selectedRevisionId,
    selectedRevisionIds,
    selectedRevisionModel,
    selectedRevisions,
    selectionLimitError,
    syncVisibleSelectedRevisions,
    toggleAllVisibleRevisions,
    toggleRevision,
  } = useDocumentRevisionSelection({
    selectionMode,
    maxSelections,
    alreadySelectedRevisionIds,
    initialSelectedRevisionId,
    initialSelectedRevisionIds,
    initialSelectedRevisions,
    selectionLimitMessage,
    availableRevisions,
    visibleRevisions,
    syncSelectedRevisionRecords: (selectedIds, selectedRecords) =>
      syncSelectedRevisionRecords(documentRows.value, selectedIds, selectedRecords),
  });

  function toggleDocumentExpansion(documentId: number): void {
    const wasExpanded = expandedDocumentIds.value.includes(documentId);
    expandedDocumentIds.value = toggleExpandedDocumentIds(expandedDocumentIds.value, documentId);

    if (onDemandRevisions && !wasExpanded) {
      const row = documentRows.value.find((item) => item.documentId === documentId);
      if (row) {
        ensurePreviousRevisionsLoaded(documentId, row.representingRevision.id);
      }
    }
  }

  function buildSearchParams() {
    const searchParams = buildDocumentSelectionSearchParams(
      searchTerm.value,
      searchFieldId.value,
      1,
      selectedAmount.value,
      searchUrl,
      { latestRevisionOnly: onDemandRevisions }
    );

    if (!enableSort && !enableAdvancedFilter && Object.keys(fixedFilters).length === 0) {
      return searchParams;
    }

    return {
      ...searchParams,
      ...(enableSort && sort.value.sort ? { sort: { ...sort.value } } : {}),
      filter: {
        ...(enableAdvancedFilter ? activeFilters.value : {}),
        ...fixedFilters,
      },
    };
  }

  async function loadDocuments(page = 1): Promise<void> {
    currentPage.value = page;
    clearSelectionLimitError();
    documentRevisionsStore.setQuantity(selectedAmount.value);
    await documentRevisionsStore.performSearch(buildSearchParams(), page);

    expandedDocumentIds.value = filterExpandedDocumentIds(
      expandedDocumentIds.value,
      documentRows.value
    );

    syncVisibleSelectedRevisions();

    if (
      selectionMode === 'single' &&
      !availableRevisions.value.some((revision) => revision.id === selectedRevisionId.value)
    ) {
      selectedRevisionId.value = null;
    }
  }

  function handleSearch(nextSearchTerm: string): void {
    searchTerm.value = nextSearchTerm;
    loadDocuments(1).catch(() => undefined);
  }

  function handleSort(sortField: string, orderFlow: string): void {
    sort.value =
      orderFlow === 'asc' || orderFlow === 'desc'
        ? { sort: sortField, order: orderFlow }
        : { sort: '', order: '' };
    void loadDocuments(1);
  }

  function handleFilterApply(filters: Record<string, unknown>): void {
    activeFilters.value = filters ?? {};
    void loadDocuments(1);
  }

  function handleDocumentFilterChange(value: string | number): void {
    selectedAmount.value = Number(value);
    documentRevisionsStore.setQuantity(selectedAmount.value);
    loadDocuments(1).catch(() => undefined);
  }

  function handlePageChanged(page: number): void {
    loadDocuments(page).catch(() => undefined);
  }

  watch(searchFieldId, () => {
    if (searchTerm.value.trim() === '') {
      loadDocuments().catch(() => undefined);
    }
  });

  onUnmounted(() => {
    documentRevisionsStore.clearCache();
    resetPreviousRevisionsCache();
  });

  useInfiniteScrollObserver({
    enabled: isInfiniteScrollMode,
    loadingMore,
    hasMore: hasMoreRevisions,
    onLoadMore: () => documentRevisionsStore.loadMoreRevisions(),
  });

  loadDocuments().catch(() => undefined);

  return {
    amountOptions,
    activeFilters,
    allVisibleRevisionsSelected,
    canAddMore,
    canSelectRevision,
    currentOrder,
    currentPage,
    currentSort,
    documentRows,
    formatRevisionLabel,
    handleDocumentFilterChange,
    handleFilterApply,
    handlePageChanged,
    handleRevisionSelected,
    handleSearch,
    handleSort,
    hasMoreRevisions,
    isInfiniteScrollMode,
    isDocumentExpanded,
    isFilterActive,
    isRevisionActive,
    isRevisionAlreadyAdded,
    isRevisionSelected,
    getPreviousRevisionsState,
    retryLoadPreviousRevisions,
    loading,
    loadingMore,
    metadata,
    orderOptions,
    searchFieldId,
    searchFields,
    searchTerm,
    selectedAmount,
    selectedRevision,
    selectedRevisionCount,
    selectedRevisionId,
    selectedRevisionIds,
    selectedRevisionModel,
    selectedRevisions,
    selectionLimitError,
    toggleAllVisibleRevisions,
    toggleDocumentExpansion,
    toggleRevision,
    visibleRevisions,
  };
}
